// GPX import: reads track points (position, elevation, time, heart rate and cadence from the Garmin
// TrackPointExtension) from a GPX file's text. Runs in the browser and in Node without a DOM parser.
// Our own small reader (decision in docs/architecture.md, "Cardio"): gpxparser (MIT) and @tmcw/togeojson
// (BSD-2) both need a DOMParser and add a package; GPX track points are simple enough to read directly.
import type { ParsedTrack, TrackSample } from './activity';

/** Larger files are refused (a 10 hour ride at one point per second is about 6 MB). */
export const MAX_GPX_BYTES = 25_000_000;
export const MAX_TRACK_SAMPLES = 200_000;

export class TrackFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TrackFileError';
  }
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
const decode = (s: string) => s.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, e: string) => {
  if (e[0] === '#') {
    const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : '';
  }
  return ENTITIES[e.toLowerCase()] ?? '';
});

const attr = (tag: string, name: string): string | undefined => new RegExp(`\\s${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(tag)?.[1];

/** The text of the first element whose local name is `name` (any namespace prefix), or undefined. */
function child(body: string, name: string): string | undefined {
  const m = new RegExp(`<(?:[\\w.-]+:)?${name}(?:\\s[^>]*)?>([^<]*)</(?:[\\w.-]+:)?${name}>`, 'i').exec(body);
  return m ? decode(m[1]!.trim()) : undefined;
}

const number = (s: string | undefined): number | undefined => {
  if (s === undefined || s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

/** Longest element body or tag we look into: real track points are a few hundred characters. */
const MAX_PART = 4096;

/**
 * Calls `each(head, body)` for every element `<tag ...>body</tag>` (or `<tag .../>`), in one linear pass:
 * each search starts where the last one ended, and a body ends at its closing tag or at the next opening tag,
 * whichever comes first, so a file full of unclosed tags cannot make the reader rescan it (no regular
 * expression runs over the whole text). Heads and bodies longer than 4 KB are cut.
 */
export function eachElement(text: string, tag: string, each: (head: string, body: string) => boolean | void): void {
  const open = `<${tag}`;
  const close = `</${tag}>`;
  let pos = text.indexOf(open);
  while (pos !== -1) {
    const after = text.charCodeAt(pos + open.length);
    // "<trkpt" must not match "<trkptx": the next character is a space, ">" or "/".
    if (!(after === 32 || after === 9 || after === 10 || after === 13 || after === 62 || after === 47)) {
      pos = text.indexOf(open, pos + open.length);
      continue;
    }
    const next = text.indexOf(open, pos + open.length);
    const limit = next === -1 ? text.length : next;
    const gt = text.slice(pos, Math.min(limit, pos + MAX_PART)).indexOf('>');
    if (gt === -1) { pos = next; continue; }
    const tagEnd = pos + gt;
    const head = text.slice(pos + open.length, Math.min(tagEnd, pos + open.length + MAX_PART));
    let body = '';
    if (text.charCodeAt(tagEnd - 1) !== 47) {
      const window = text.slice(tagEnd + 1, Math.min(limit, tagEnd + 1 + MAX_PART));
      const c = window.indexOf(close);
      body = c === -1 ? window : window.slice(0, c);
    }
    if (each(head, body) === false) return;
    pos = next;
  }
}

/** The first `<tag>` and up to 4 KB after it, or ''. */
function firstPart(text: string, tag: string): string {
  let body = '';
  eachElement(text, tag, (_h, b) => { body = b; return false; });
  return body;
}

export function parseGpx(text: string): ParsedTrack {
  if (text.length > MAX_GPX_BYTES) throw new TrackFileError('This GPX file is larger than 25 MB.');
  if (!/<gpx[\s>]/i.test(text)) throw new TrackFileError('This is not a GPX file (no <gpx> element).');
  // The track's own name and type (before its first segment), else the file's metadata name.
  const trk = firstPart(text, 'trk').split('<trkseg')[0]!;
  const name = child(trk, 'name') ?? child(firstPart(text, 'metadata'), 'name');
  const type = child(trk, 'type');

  const samples: TrackSample[] = [];
  // Track points, or route points when the file is a planned route without a recording.
  const tag = text.includes('<trkpt') ? 'trkpt' : 'rtept';
  let tooMany = false;
  eachElement(text, tag, (head, body) => {
    if (samples.length >= MAX_TRACK_SAMPLES) { tooMany = true; return false; }
    const lat = number(attr(head, 'lat'));
    const lon = number(attr(head, 'lon'));
    if (lat === undefined || lon === undefined || Math.abs(lat) > 90 || Math.abs(lon) > 180) return;
    const s: TrackSample = { lat, lon };
    const ele = number(child(body, 'ele'));
    if (ele !== undefined) s.ele = ele;
    const time = child(body, 'time');
    if (time) {
      const t = Date.parse(time);
      if (Number.isFinite(t)) s.time = t;
    }
    const hr = number(child(body, 'hr') ?? child(body, 'heartrate'));
    if (hr !== undefined && hr > 0 && hr < 260) s.hr = hr;
    const cad = number(child(body, 'cad') ?? child(body, 'cadence'));
    if (cad !== undefined && cad >= 0 && cad < 400) s.cadence = cad;
    samples.push(s);
  });
  if (tooMany) throw new TrackFileError(`This GPX file has more than ${MAX_TRACK_SAMPLES} points.`);
  if (samples.length === 0) throw new TrackFileError('This GPX file has no track points.');
  return {
    format: 'gpx',
    ...(name ? { name: name.slice(0, 120) } : {}),
    ...(type ? { sportHint: type } : {}),
    samples,
  };
}
